import { Injectable } from '@angular/core';
import { OeAppPersonService } from './oe-app-person.service';
import { ProfileServiceConfigurationService } from './profile-service-configuration.service';
import { TranslationService } from './translation.service';
import { NotificationProvider } from './types/ConfigurationsDto';
import { AnalyticsOutcomeEvents } from './types/analytics-outcome.types';
import { OneSignalTags } from './types/one-signal-tags';
import { OneSignalNotificationEventService } from './one-signal-notification-event.service';

declare global {
  interface Window {
    OneSignalDeferred: any[];
  }
}

@Injectable()
export class OneSignalService {
  private oneSignal: any;

  constructor(
    private oeAppPersonService: OeAppPersonService,
    private translationService: TranslationService,
    private profileServiceConfigurationService: ProfileServiceConfigurationService,
    private oneSignalNotificationEventService: OneSignalNotificationEventService
  ) {}

  public init() {
    console.log(process.env.NODE_ENV);
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    this.profileServiceConfigurationService.notificationPushConfig$().subscribe((config) => {
      if (!config) {
        return;
      }
      const { notificationWebPushProvider, oneSignalAppId } = config;
      if (notificationWebPushProvider === NotificationProvider.ONESIGNAL) {
        const config = this.getConfig(oneSignalAppId);
        if (config) {
          this.register(config);
        }
      } else {
        this.unregister();
      }
    });
  }

  public logout() {
    if (this.oneSignal) {
      this.oneSignal.logout();
    }
    this.unregister();
  }

  public trackOutcome(outcome: AnalyticsOutcomeEvents) {
    const { notificationWebPushProvider } = this.profileServiceConfigurationService.state;
    if (this.oneSignal && notificationWebPushProvider === NotificationProvider.ONESIGNAL) {
      this.oneSignal.Session.sendOutcome(outcome);
    }
  }

  public setTag(tag: OneSignalTags, value: string) {
    if (this.oneSignal && this.oneSignal.User) {
      this.oneSignal.User.addTag(tag, value);
    }
  }

  private getConfig(oneSignalAppId: string) {
    if (!oneSignalAppId) {
      return;
    }
    return {
      appId: oneSignalAppId,
      serviceWorkerParam: { scope: '/push/onesignal/' },
      serviceWorkerPath: 'OneSignalSDKWorker.js',
      notificationClickHandlerMatch: 'origin',
      notificationClickHandlerAction: 'focus',
      welcomeNotification: {
        disable: true,
      },
      promptOptions: {
        siteName: 'Open English',
        slidedown: {
          prompts: [
            {
              type: 'push',
              autoPrompt: true,
              delay: {
                pageViews: 1,
                timeDelay: 10,
              },
              text: {
                actionMessage: this.translationService.translate('notifications.permissionRequest.actionMessage'),
                acceptButton: this.translationService.translate('notifications.permissionRequest.acceptButton'),
                cancelButton: this.translationService.translate('notifications.permissionRequest.cancelButton'),
              },
            },
          ],
        },
      },
    };
  }

  private async register(config: any) {
    if (!!config) {
      var script = document.createElement('script');
      script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
      script.defer = true;

      script.onload = () => {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OneSignal) => {
          this.oneSignal = OneSignal;
          await this.oneSignal.init(config);
          this.oneSignal.login(this.oeAppPersonService.contactId);
          this.oneSignal.Notifications.addEventListener("click", (event) => {
            this.oneSignalNotificationEventService.emitNotificationClick(event.notification.launchURL);
          });
        });
      };
      document.head.appendChild(script);
    }
  }

  private unregister() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
          if (registration.scope.includes('/push/onesignal/')) {
            registration.unregister();
          }
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/one-signal.service.ts